import { useCallback, useMemo } from "react";
import { debounce } from "@perxis-auto/shared/lib/lib/delays/debounce";
import { isDefined } from "@perxis-auto/shared/lib/lib/is/isDefined";
import { actions } from "../internal/features/choice/model/slice.js";
import { CreditType } from "../internal/shared/api/types.gen.js";
import { CREDIT_LIMITS_FORM_FIELDS, DEFAULT_CREDIT_LIMITS_VALUES } from "../internal/shared/config/credit.js";
import { useAppDispatch } from "../internal/shared/lib/store/hooks/useAppDispatch.js";
import { calculatePercentageOfValue } from "../internal/shared/lib/utils/calculatePercentageOfValue.js";
import { calculateValueFromPercentage } from "../internal/shared/lib/utils/calculateValueFromPercentage.js";
import { formatPaymentValue } from "../internal/shared/lib/utils/formatPaymentValue.js";
import { normalizeRangeValue } from "../internal/shared/lib/utils/normalizeRangeValue.js";
import { parsePaymentValue } from "../internal/shared/lib/utils/parsePaymentValue.js";
import { useCreditChoice } from "./useCreditChoice.js";
import { useCreditLimits } from "./useCreditLimits.js";
function useCreditUtils({ form, debouncedUpdateValuesHandler }) {
  var _a, _b;
  const dispatch = useAppDispatch();
  const { data: creditLimits } = useCreditLimits();
  const { data: choice } = useCreditChoice();
  const downPaymentMin = (_a = creditLimits == null ? void 0 : creditLimits.downPayment) == null ? void 0 : _a.min;
  const residualPaymentMin = (_b = creditLimits == null ? void 0 : creditLimits.residualPayment) == null ? void 0 : _b.min;
  const sliderValueNormalize = useCallback(
    (scope) => (value) => {
      if (!scope) {
        return null;
      }
      return normalizeRangeValue(value, scope);
    },
    []
  );
  const downPaymentNormalize = useMemo(
    () => sliderValueNormalize(creditLimits == null ? void 0 : creditLimits.downPayment),
    [creditLimits == null ? void 0 : creditLimits.downPayment, sliderValueNormalize]
  );
  const residualPaymentNormalize = useMemo(
    () => sliderValueNormalize(creditLimits == null ? void 0 : creditLimits.residualPayment),
    [creditLimits == null ? void 0 : creditLimits.residualPayment, sliderValueNormalize]
  );
  const paymentTipFormatter = useCallback((value) => {
    const percent = typeof value === "number" ? Math.round(value) : 0;
    return `${percent}%`;
  }, []);
  const periodTipFormatter = useCallback((value) => {
    const years = Math.round(value / 12 * 10) / 10;
    return years;
  }, []);
  const paymentValueFormatter = useCallback(
    (percent) => {
      const amount = calculateValueFromPercentage(choice == null ? void 0 : choice.price, percent);
      if (!isDefined(amount)) {
        return;
      }
      return amount;
    },
    [choice == null ? void 0 : choice.price]
  );
  const createFieldUpdater = useCallback(
    (percentageField, amountField, normalizeFn) => {
      return (percentage, amount, updateBothFields = true) => {
        let resolvedPercentage = null;
        let resolvedAmount = null;
        if (isDefined(percentage) && !isDefined(amount)) {
          resolvedPercentage = normalizeFn(percentage);
        }
        if (isDefined(amount) && !isDefined(percentage)) {
          resolvedPercentage = normalizeFn(
            Number(calculatePercentageOfValue(parsePaymentValue(amount), choice == null ? void 0 : choice.price))
          );
        }
        resolvedAmount = calculateValueFromPercentage(choice == null ? void 0 : choice.price, resolvedPercentage);
        if (updateBothFields) {
          form.setFieldsValue({
            [amountField]: formatPaymentValue(resolvedAmount)
          });
        }
        form.setFieldsValue({
          [percentageField]: resolvedPercentage
        });
        return {
          [percentageField]: resolvedPercentage,
          [amountField]: resolvedAmount
        };
      };
    },
    [choice.price, form]
  );
  const updateDownPayment = useMemo(
    () => createFieldUpdater(
      CREDIT_LIMITS_FORM_FIELDS.downPayment,
      CREDIT_LIMITS_FORM_FIELDS.downPaymentAmount,
      downPaymentNormalize
    ),
    [createFieldUpdater, downPaymentNormalize]
  );
  const updateResidualPayment = useMemo(
    () => createFieldUpdater(
      CREDIT_LIMITS_FORM_FIELDS.residualPayment,
      CREDIT_LIMITS_FORM_FIELDS.residualPaymentAmount,
      residualPaymentNormalize
    ),
    [createFieldUpdater, residualPaymentNormalize]
  );
  const handleDebouncedUpdate = useMemo(
    () => debounce((resolvedValues) => {
      debouncedUpdateValuesHandler == null ? void 0 : debouncedUpdateValuesHandler(resolvedValues, choice);
      dispatch(actions.setCreditParameters(resolvedValues));
    }, 500),
    [dispatch, debouncedUpdateValuesHandler, choice]
  );
  const handleValuesChange = useCallback(
    (changedValues, allValues) => {
      const {
        type: changedType,
        downPaymentAmount: changedDownPaymentAmount,
        residualPaymentAmount: changedResidualPaymentAmount
      } = changedValues;
      let { downPayment: changedDownPayment, residualPayment: changedResidualPayment } = changedValues;
      let resolvedValues = { ...changedValues };
      const { type, downPayment, residualPayment } = allValues;
      if (changedType === CreditType.ANNUITY) {
        resolvedValues.residualPayment = null;
        resolvedValues.residualPaymentAmount = null;
      }
      if (changedType === CreditType.RESIDUAL_PAYMENT) {
        resolvedValues = {
          ...resolvedValues,
          ...updateResidualPayment(Number(DEFAULT_CREDIT_LIMITS_VALUES.residualPayment))
        };
      }
      if (typeof changedDownPayment !== "undefined") {
        resolvedValues = {
          ...resolvedValues,
          ...updateDownPayment(Number(changedDownPayment))
        };
      }
      if (typeof changedDownPaymentAmount !== "undefined") {
        const downPaymentAmount = parsePaymentValue(changedDownPaymentAmount);
        resolvedValues = {
          ...resolvedValues,
          ...updateDownPayment(
            Number.isNaN(downPaymentAmount) ? downPaymentMin : void 0,
            Number.isNaN(downPaymentAmount) ? void 0 : downPaymentAmount,
            false
          )
        };
        changedDownPayment = resolvedValues.downPayment;
      }
      if (typeof changedResidualPayment !== "undefined") {
        resolvedValues = {
          ...resolvedValues,
          ...updateResidualPayment(Number(changedResidualPayment))
        };
      }
      if (typeof changedResidualPaymentAmount !== "undefined") {
        const residualPaymentAmount = parsePaymentValue(changedResidualPaymentAmount);
        resolvedValues = {
          ...resolvedValues,
          ...updateResidualPayment(
            Number.isNaN(residualPaymentAmount) ? residualPaymentMin : void 0,
            Number.isNaN(residualPaymentAmount) ? void 0 : residualPaymentAmount,
            false
          )
        };
        changedResidualPayment = resolvedValues.residualPayment;
      }
      if (typeof changedDownPayment !== "undefined" && Number(changedDownPayment) + Number(residualPayment) >= 99) {
        resolvedValues = {
          ...resolvedValues,
          ...updateResidualPayment(99 - Number(changedDownPayment))
        };
      }
      if (typeof changedResidualPayment !== "undefined" && Number(changedResidualPayment) + Number(downPayment) >= 99) {
        resolvedValues = {
          ...resolvedValues,
          ...updateDownPayment(99 - Number(changedResidualPayment))
        };
      }
      if (type === CreditType.RESIDUAL_PAYMENT && typeof residualPaymentMin !== "undefined" && Number(changedDownPayment) > 99 - residualPaymentMin) {
        resolvedValues = {
          ...resolvedValues,
          ...updateDownPayment(99 - Number(residualPaymentMin))
        };
        resolvedValues = {
          ...resolvedValues,
          ...updateResidualPayment(residualPaymentMin)
        };
      }
      if (type === CreditType.RESIDUAL_PAYMENT && typeof downPaymentMin !== "undefined" && Number(changedResidualPayment) > 99 - downPaymentMin) {
        resolvedValues = {
          ...resolvedValues,
          ...updateResidualPayment(99 - Number(downPaymentMin))
        };
        resolvedValues = {
          ...resolvedValues,
          ...updateDownPayment(downPaymentMin)
        };
      }
      void handleDebouncedUpdate(resolvedValues);
    },
    [downPaymentMin, handleDebouncedUpdate, residualPaymentMin, updateDownPayment, updateResidualPayment]
  );
  return {
    formatters: {
      paymentValueFormatter,
      paymentTipFormatter,
      periodTipFormatter
    },
    normalizers: {
      downPaymentNormalize,
      residualPaymentNormalize
    },
    handles: {
      handleValuesChange
    }
  };
}
export {
  useCreditUtils
};
